import { useContext, useEffect, useMemo, useState } from "react";
import { allOrdersPath, MODEL_COMPLEX_PATH, userOrdersPath } from "../../../functions/src/admin/paths";
import { groupStaleBasketEntriesByCategory, isOrderDayWithinBasketStaleAttentionWindow, type StaleBasketGroup } from "../../../functions/src/common/basketStaleAttention";
import { isCommittedOrder, normalizeOrderContent, orderHasContentLines } from "../../../functions/src/common/orders";
import { getCheckoutDayAllowedResult, isCheckoutBlockedOnlyBySetMenuMealsInDraft, type CheckoutDayBlockCategory } from "../../../functions/src/common/orderDeadlines";
import type { ModelComplexity, Order } from "../../../functions/src/types";
import { auth, schoolDatabase } from "../../api/firebase";
import { useDatabase, useDatabaseVal } from "../../api/firebaseHooks";
import { langContext } from "../../api/Lang";
import { useServerTimeOffsetMs } from "../../api/useServerNow";
import { useOrderDayDeadlineContext } from "../useOrderDayDeadlineContext";
import { useBasketServerNow } from "./useBasketServerNow";

export type BasketEntryKind = 'new' | 'change';

export type BasketEntry = {
    date: string;
    kind: BasketEntryKind;
    draft: Order;
    placed: Order | null;
    /** Placed order price refunded at commit (0 for new days). */
    oldPrice: number;
};

export type StaleBasketFixKind = 'remove_day' | 'remove_set_menu_meals';

export type StaleBasketEntry = BasketEntry & {
    message: string;
    category: CheckoutDayBlockCategory;
    /** How the parent can fix this day without dropping valid daily meals / snacks. */
    fixKind: StaleBasketFixKind;
};

export { normalizeOrderContent, orderHasContentLines };

/**
 * Live committed orders for basket candidate dates (`orders/{date}/{uid}`).
 * One small listener per date while that date is a candidate; payloads are tiny and change rarely.
 */
function usePlacedOrdersMap(dates: string[]): { [date: string]: Order | null } {
    const uid = auth.currentUser?.uid;
    const [placed, setPlaced] = useState<{ [date: string]: Order | null }>({});
    const datesKey = dates.join(',');

    useEffect(() => {
        setPlaced({});
        if (!uid || !datesKey) return undefined;

        const dateList = datesKey.split(',').filter(Boolean);
        if (!dateList.length) return undefined;

        const cleanups = dateList.map(date => {
            const ref = schoolDatabase.ref(allOrdersPath(uid, date));
            const handler = ref.on('value',
                snap => setPlaced(prev => ({ ...prev, [date]: snap.val() ?? null })),
                err => console.error(err)
            );
            return () => ref.off('value', handler);
        });
        return () => { cleanups.forEach(off => off()); };
    }, [uid, datesKey]);

    return placed;
}

export type BasketState = {
    entries: BasketEntry[];
    staleEntries: StaleBasketEntry[];
    staleGroups: StaleBasketGroup[];
    /** Actionable + stale days (header badge). */
    totalBasketDays: number;
    /** True while drafts / placed orders are still resolving. */
    entriesLoading: boolean;
    modelConstants: ModelComplexity | null;
    /** Server now for deadline checks — updates while pending basket days exist. */
    basketServerNow: Date | undefined;
};

/**
 * Basket membership: drafts with pending work split into checkout-valid entries vs stale “needs attention”.
 */
export function useBasketEntries(): BasketState {
    const [lang] = useContext(langContext);
    const uid = auth.currentUser?.uid;
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const deadlineCtx = useOrderDayDeadlineContext();
    const serverOffsetMs = useServerTimeOffsetMs();

    const sDrafts = useDatabase(uid ? userOrdersPath(uid) : null);

    const candidates = useMemo(() => {
        if (serverOffsetMs === undefined) {
            return [];
        }
        const result: { date: string; draft: Order }[] = [];
        sDrafts?.forEach(child => {
            const date = child.key;
            const draft = child.val() as Order;
            if (orderHasContentLines(draft)) {
                result.push({ date, draft });
            }
        });
        return result.sort((a, b) => a.date.localeCompare(b.date));
    }, [sDrafts, serverOffsetMs]);

    const basketServerNow = useBasketServerNow(candidates.length > 0);

    const placedMap = usePlacedOrdersMap(useMemo(() => candidates.map(c => c.date), [candidates]));

    const { entries, staleEntries } = useMemo(() => {
        const valid: BasketEntry[] = [];
        const stale: StaleBasketEntry[] = [];
        if (!deadlineCtx || !basketServerNow) {
            return { entries: valid, staleEntries: stale };
        }

        for (const { date, draft } of candidates) {
            const placed = placedMap[date];
            if (placed === undefined) {
                continue;
            }

            let kind: BasketEntryKind;
            let oldPrice = 0;
            if (!isCommittedOrder(placed)) {
                kind = 'new';
            } else if (normalizeOrderContent(draft) !== normalizeOrderContent(placed)) {
                kind = 'change';
                oldPrice = placed.price ?? 0;
            } else {
                continue;
            }

            const checkout = getCheckoutDayAllowedResult(date, draft, placed, deadlineCtx, lang, basketServerNow);
            const row: BasketEntry = { date, kind, draft, placed: isCommittedOrder(placed) ? placed : null, oldPrice };
            if (checkout.ok === true) {
                valid.push(row);
            } else if (isOrderDayWithinBasketStaleAttentionWindow(date, deadlineCtx.modelConstants, basketServerNow)) {
                const fixKind: StaleBasketFixKind = isCheckoutBlockedOnlyBySetMenuMealsInDraft(
                    date, draft, placed, deadlineCtx, lang, basketServerNow,
                )
                    ? 'remove_set_menu_meals'
                    : 'remove_day';
                stale.push({ ...row, message: checkout.message, category: checkout.category, fixKind });
            }
        }

        return { entries: valid, staleEntries: stale };
    }, [candidates, placedMap, deadlineCtx, basketServerNow, lang]);

    const staleGroups = useMemo(
        () => groupStaleBasketEntriesByCategory(staleEntries.map(({ date, kind, message, category }) => ({ date, kind, message, category }))),
        [staleEntries],
    );

    const entriesLoading = sDrafts === undefined || candidates.some(c => placedMap[c.date] === undefined);

    return {
        entries,
        staleEntries,
        staleGroups,
        totalBasketDays: entries.length + staleEntries.length,
        entriesLoading,
        modelConstants: modelConstants ?? null,
        basketServerNow,
    };
}
